import React, { useEffect, useState } from "react";
import AiGenerator from '../activities/AiGenerator';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, IconButton, Box, Typography,
    Select, MenuItem, InputAdornment,
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

interface AssignActivityModalProps {
    open: boolean;
    onClose: () => void;
    employeeId?: number | null;
    onAssign?: (payload: any) => Promise<any> | void;
    categories: any[];
    technologies: any[];
    employees: any[];
    activities: any[];
}


const AssignActivityModal: React.FC<AssignActivityModalProps> = ({
    open, onClose, onAssign,
    categories, technologies, employees, activities
}) => {
    const [form, setForm] = useState<any>({
        activityId: 0,
        categoryId: 0,
        technologyId: 0,
        name: "",
        description: "",
        pointOfContactId: 0,
        hours: 0,
        status: "Yet to start"
    });
    const [categoryInput, setCategoryInput] = useState("");
    const [technologyInput, setTechnologyInput] = useState("");
    const [pocInput, setPocInput] = useState("");
    const [isExistingActivity, setIsExistingActivity] = useState(false);
    const [showAiGenerator, setShowAiGenerator] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const snackbarTimer = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!open) {
            setForm({
                activityId: 0,
                categoryId: 0,
                technologyId: 0,
                name: "",
                description: "",
                pointOfContactId: 0,
                hours: 0,
                status: "Yet to start"
            });
            setCategoryInput("");
            setTechnologyInput("");
            setPocInput("");
            setIsExistingActivity(false);
            setErrors({});
            setShowAiGenerator(false);
        }
    }, [open]);

    const handleChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const sanitizeNameInput = (value: string) =>
        value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');

    const validate = () => {
        const e: any = {};
        if (!form.name?.trim()) e.name = "Activity required";
        else if (form.name.length > 80) e.name = "Please limit to 80 characters";
        else if (!/[a-zA-Z]/.test(form.name)) e.name = "Activity name cannot be only numbers or special characters";
        if (!isExistingActivity && !form.categoryId) e.category = "Category required";
        if (!isExistingActivity && !form.technologyId) e.technology = "Technology required";
        if (!isExistingActivity && !form.description?.trim()) e.description = "Description required";
        if (!form.pointOfContactId) e.poc = "Point of contact required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const resetForm = () => {
        setForm({
            activityId: 0,
            categoryId: 0,
            technologyId: 0,
            name: "",
            description: "",
            pointOfContactId: 0,
            hours: 0,
            status: "Yet to start"
        });
        setIsExistingActivity(false);
        setErrors({});
        setSnackbar({ open: false, message: "" });
    };

    const handleAssign = async () => {
        const showSnackbar = (msg: string) => {
            setSnackbar({ open: true, message: msg });
            if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
            snackbarTimer.current = setTimeout(() => {
                setSnackbar({ open: false, message: "" });
            }, 3000);
        };
        const valid = validate();
        // Only show the global error if any required field is empty
        const requiredFields = [
            form.name?.trim(),
            isExistingActivity ? true : form.categoryId,
            isExistingActivity ? true : form.technologyId,
            isExistingActivity ? true : form.description?.trim(),
            form.pointOfContactId
        ];
        const hasEmptyRequired = requiredFields.some(f => !f);
        if (!valid && hasEmptyRequired) {
            showSnackbar("Please fill required fields");
            return;
        }
        if (!valid) {
            // Only inline errors will show
            return;
        }
        if (!isExistingActivity) {
            const normalize = (name: string) => (name || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
            const inputNorm = normalize(form.name);
            // Check for inactive activity with same name
            const inactiveExists = (activities || []).some((a: any) => {
                const status = String(a.status || a.activityStatus || a.activityState || '').toLowerCase();
                const norm = normalize(a.activityName ?? a.name);
                return norm === inputNorm && status !== 'active';
            });
            if (inactiveExists) {
                showSnackbar("This activity already exists and is currently inactive. Please assign a different activity.");
                return;
            }
            // Check for active duplicate (existing logic)
            const duplicate = (activities || []).some((a: any) => {
                const status = String(a.status || a.activityStatus || a.activityState || '').toLowerCase();
                if (status !== 'active') return false;
                const norm = normalize(a.activityName ?? a.name);
                return norm === inputNorm;
            });
            if (duplicate) {
                showSnackbar("An activity with this name already exists.");
                return;
            }
        }
        try {
            setSaving(true);

            const result = await onAssign?.({
                activityId: form.activityId,
                name: form.name,
                categoryId: form.categoryId,
                technologyId: form.technologyId,
                description: form.description,
                pointOfContactId: form.pointOfContactId,
                status: "Yet to start",
                isNew: true
            });

            if (result && result.duplicate) {
                showSnackbar(result.message || 'Duplicate assignment');
                return;
            }

            onClose();
        } catch {
            showSnackbar("Error saving activity");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (open) resetForm();
    }, [open]);

    const normalize = (name: string) => (name || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
    const seen = new Set<string>();
    const activityOptions = (activities || [])
        .filter((a: any) => {
            const status = String(a.status || a.activityStatus || a.activityState || '').toLowerCase();
            return status === 'active';
        })
        .map((a: any) => ({
    activityId: a.activityId ?? a.id,
    activityName: a.activityName ?? a.name,
    activityDescription: a.activityDescription ?? a.description,

    categoryId: a.categoryId,
    categoryName:
        a.categoryName ??
        a.category?.name ??
        a.category ??
        "",

    technologyId: a.technologyId,
    technologyName:
        a.technologyName ??
        a.technology?.name ??
        a.technology ??
        "",

    pointOfContactId: a.pointOfContactId
}))
        .filter((a: any) => {
            const norm = normalize(a.activityName);
            if (seen.has(norm)) return false;
            seen.add(norm);
            return true;
        });

    const sortedActivityOptions = activityOptions.slice().sort((x: any, y: any) =>
        String(x.activityName || '').trim().toLowerCase().localeCompare(String(y.activityName || '').trim().toLowerCase())
    );


    // Dark mode detection (prefer MUI theme if available, fallback to media query)
    const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    // If you use MUI theme, you can use: const theme = useTheme(); const isDark = theme.palette.mode === 'dark';
    const isDark = prefersDark;

    // ── Compact shared style tokens ──
    const labelStyle: React.CSSProperties = {
        fontSize: '12px',
        fontWeight: 500,
        color: isDark ? '#fff' : '#1a1a1a',
        marginBottom: '4px',
        display: 'block',
    };
    const requiredStar: React.CSSProperties = { color: '#d32f2f', marginLeft: '2px' };
    const fieldWrap: React.CSSProperties = { marginBottom: '10px' };
    // Inline error style for dark mode
    const errorTextStyle: React.CSSProperties = isDark ? { color: '#d32f2f' } : {};
    const inputSx = {
        borderRadius: '6px',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        color: isDark ? '#f1f5f9' : undefined,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#d0d5dd' },
        '& .MuiInputBase-root': { fontSize: '12px', color: isDark ? '#f1f5f9' : undefined },
        '& .MuiInputBase-input': { padding: '6px 8px', color: isDark ? '#f1f5f9' : undefined, WebkitTextFillColor: isDark ? '#f1f5f9' : undefined },
        '& textarea': { color: isDark ? '#f1f5f9' : undefined, WebkitTextFillColor: isDark ? '#f1f5f9' : undefined },
        // For disabled fields in dark mode, use a less prominent color
        ...(isDark && {
            '& .Mui-disabled': {
                color: '#94a3b8',
                WebkitTextFillColor: '#94a3b8',
                opacity: 1,
            },
        }),
    };
    const dropdownMenuSx = {
        backgroundColor: isDark ? '#1e293b' : '#fff',
        color: isDark ? '#f1f5f9' : '#1e293b',
    };
    const statusStyle = {
        borderRadius: '6px',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        fontSize: '12px',
        height: '34px',
        color: isDark ? '#f1f5f9' : undefined,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#d0d5dd' },
        '& .MuiSelect-select': {
            padding: '6px 8px !important',
            fontSize: '12px',
            lineHeight: '1.4',
            display: 'flex',
            alignItems: 'center',
        },
        ...(isDark && {
            '& .MuiSelect-icon': { color: '#cbd5f5' },
        }),
    };

    // Only close modal on X or Cancel, not on backdrop or escape
    const handleDialogClose = (event: object, reason: string) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        resetForm();
        onClose();
    };
    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: 0,
                    maxHeight: '90vh',
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                },
            }}
            disableEscapeKeyDown
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isDark ? '1px solid #334155' : '1px solid #e0e0e0',
                    py: 1.5,
                    px: 3,
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                }}
            >
                <Typography fontWeight={700} fontSize={16} sx={{ color: isDark ? '#fff' : '#124B84' }}>
                    Assign Activity
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: isDark ? '#38bdf8' : '#124B84' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Form ── */}
            <DialogContent sx={{ px: 3, pt: 1.5, pb: 0, backgroundColor: isDark ? '#1e293b' : '#fff' }}>

                {/* 1. Name */}
                <div style={fieldWrap}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', justifyContent: 'space-between' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                            Name<span style={requiredStar}>*</span>
                        </label>
                        {!showAiGenerator && (
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<AutoAwesomeIcon sx={{ color: '#a259f7', fontSize: 18 }} />}
                                sx={{
                                    color: '#a259f7',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: 13,
                                    minWidth: 0,
                                    p: 0,
                                    ml: 2,
                                    lineHeight: 1,
                                    background: 'none',
                                    boxShadow: 'none',
                                    letterSpacing: 0.1,
                                    '&:hover': {
                                        background: 'none',
                                        color: '#7c2ae8',
                                        textDecoration: 'underline',
                                    },
                                }}
                                onClick={() => setShowAiGenerator(true)}
                            >
                                Create using AI
                            </Button>
                        )}
                    </div>
                    {showAiGenerator && (
                        <AiGenerator
                            onGenerate={({ name, description }) => {
                                setForm((prev: any) => ({ ...prev, name, description }));
                                setShowAiGenerator(false);
                            }}
                            onClose={() => setShowAiGenerator(false)}
                            category={categories.find(c => c.id === form.categoryId)?.name}
                            technology={technologies.find(t => t.id === form.technologyId)?.name}
                        />
                    )}
                    <Autocomplete
                        freeSolo
                        options={sortedActivityOptions}
                        getOptionLabel={(o: any) => typeof o === "string" ? o : o?.activityName || ""}
                        inputValue={form.name}
                        value={
                            isExistingActivity
                                ? activityOptions.find(a => a.activityId === form.activityId) || null
                                : form.name
                        }
                        onChange={(_, value: any) => {
                            if (!value) {
                                setIsExistingActivity(false);
                                setForm({
                                    activityId: 0,
                                    name: "",
                                    categoryId: 0,
                                    technologyId: 0,
                                    description: "",
                                    pointOfContactId: 0,
                                    hours: 0,
                                    status: "Yet to start"
                                });
                                setCategoryInput("");
                                setTechnologyInput("");
                                setPocInput("");
                                setErrors({});
                                return;
                            }
                            if (typeof value === "string") {
                                setIsExistingActivity(false);
                                setForm({
                                    activityId: 0,
                                    name: sanitizeNameInput(value).slice(0, 50),
                                    categoryId: 0,
                                    technologyId: 0,
                                    description: "",
                                    pointOfContactId: 0,
                                    hours: 0,
                                    status: "Yet to start"
                                });
                                setCategoryInput("");
                                setTechnologyInput("");
                                setPocInput("");
                                setErrors({});
                                return;
                            }
                            setIsExistingActivity(true);
                            setForm({
                                activityId: value.activityId,
                                name: (value.activityName || '').slice(0, 50),
                                description: value.activityDescription,
                                categoryId: value.categoryId,
                                technologyId: value.technologyId,
                                pointOfContactId: value.pointOfContactId || 0,
                                hours: 0,
                                status: "Active"
                            });
                            setCategoryInput(
    categories.find(c => c.id === value.categoryId)?.name ||
    value.categoryName ||
    ""
);
                            setTechnologyInput(
    technologies.find(t => t.id === value.technologyId)?.name ||
    value.technologyName ||
    ""
);
                            setPocInput(employees.find(e => e.empCode === value.pointOfContactId || e.employeeId === value.pointOfContactId || e.id === value.pointOfContactId)?.displayName || "");
                            setErrors({});
                        }}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input") {
                                setIsExistingActivity(false);
                                setForm((prev: any) => ({
    ...prev,
    activityId: 0,
    name: sanitizeNameInput(value).slice(0, 50)
}));
                                setErrors((prev: any) => {
                                    const { name, ...rest } = prev;
                                    return rest;
                                });
                            }
                            if (value === "") {
                                setIsExistingActivity(false);
                                setForm({
                                    activityId: 0,
                                    name: "",
                                    categoryId: 0,
                                    technologyId: 0,
                                    description: "",
                                    pointOfContactId: 0,
                                    hours: 0,
                                    status: "Yet to start"
                                });
                                setCategoryInput("");
                                setTechnologyInput("");
                                setPocInput("");
                                setErrors({});
                            }
                        }}
                        size="small"
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    placeholder="Activity Name"
                                    error={!!errors.name}
                                    helperText={
                                        errors.name && isDark
                                            ? <span style={errorTextStyle}>{errors.name}</span>
                                            : errors.name
                                    }
                                    FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                                    sx={inputSx}
                                />
                                <Typography variant="caption" className="character-count" sx={{ fontSize: '11px', mt: 0.5, ml: 0 }}>
                                    {form.name.length}/50 characters
                                </Typography>
                            </>
                        )}
                    />
                </div>

                {/* 2. Category */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Category<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={(categories || [])
    .filter((c: any) =>
        !isExistingActivity
            ? String(c.status || '').toLowerCase() === 'active'
            : true
    )
    .slice()
    .sort((a: any, b: any) =>
        String(a.name || '').trim().toLowerCase()
            .localeCompare(String(b.name || '').trim().toLowerCase())
    )
}
                        disabled={isExistingActivity}
                        value={
    form.categoryId
        ? categories.find(c => c.id === form.categoryId) || null
        : null
}
                        inputValue={
    categoryInput ||
    categories.find(c => c.id === form.categoryId)?.name ||
    ""
}
                        onInputChange={(_, v) => setCategoryInput(v)}    
                        getOptionLabel={(o: any) => typeof o === 'string' ? o : o.name || ""}
                        onChange={(_, v: any) => {
                            handleChange("categoryId", v?.id || 0);
                            setCategoryInput(v?.name || "");
                            if (v?.id) {
                                setErrors((prev: any) => {
                                    const { category, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                        clearOnEscape
                        clearIcon={null}
                        size="small"
                        renderInput={(params) => (
    <TextField
        {...params}
        placeholder="Select"
        error={!!errors.category}
        helperText={
            errors.category && isDark
                ? <span style={errorTextStyle}>{errors.category}</span>
                : errors.category
        }
        FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
        sx={inputSx}
    InputProps={{
        ...params.InputProps,
        endAdornment: (
            <>
                {categoryInput && !isExistingActivity && (
                    
                        <IconButton
                            size="small"
                            onClick={() => {
                                setCategoryInput("");
                                handleChange("categoryId", 0);
                            }}
                            edge="end"
                            sx={{
                                color: '#6b7280',
                                padding: 0,
                                position:'absolute',
                                right: '38px',
                            }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                )}

                {params.InputProps.endAdornment}
            </>
        ),
    }}
                            />
                        )}
                    />
                </div>

                {/* 3. Technology */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Technology<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={(technologies || [])
    .filter((t: any) =>
        !isExistingActivity
            ? String(t.status || '').toLowerCase() === 'active'
            : true
    )
    .slice()
    .sort((a: any, b: any) =>
        String(a.name || '').trim().toLowerCase()
            .localeCompare(String(b.name || '').trim().toLowerCase())
    )
}
                        disabled={isExistingActivity}
                        value={
    form.technologyId
        ? technologies.find(t => t.id === form.technologyId) || null
        : null
}
                        inputValue={
    technologyInput ||
    technologies.find(t => t.id === form.technologyId)?.name ||
    ""
}
                        onInputChange={(_, v) => setTechnologyInput(v)}
                        getOptionLabel={(o: any) => typeof o === 'string' ? o : o?.name || ""}
                        onChange={(_, v: any) => {
                            setForm((prev: any) => ({ ...prev, technologyId: v?.id || 0 }));
                            setTechnologyInput(v?.name || "");
                            if (v?.id) {
                                setErrors((prev: any) => {
                                    const { technology, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                        clearOnEscape
                        clearIcon={null}
                        size="small"
                        renderInput={(params) => (
    <TextField
        {...params}
        placeholder="Select"
        error={!!errors.technology}
        helperText={
            errors.technology && isDark
                ? <span style={errorTextStyle}>{errors.technology}</span>
                : errors.technology
        }
        FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
        sx={inputSx}
    InputProps={{
        ...params.InputProps,
        endAdornment: (
            <>
                {technologyInput && !isExistingActivity && (
                    
                        <IconButton
                            size="small"
                            onClick={() => {
                                setTechnologyInput("");
                                handleChange("technologyId", 0);
                            }}
                            edge="end"
                            sx={{
                                color: '#6b7280',
                                padding: 0,
                                position:'absolute',
                                right: '38px',
                            }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    
                )}

                {params.InputProps.endAdornment}
            </>
        ),
    }}
                            />
                        )}
                    />
                </div>

                {/* 4. Description */}
                <div style={fieldWrap}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={labelStyle}>
                            Description<span style={requiredStar}>*</span>
                        </label>
                    </div>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Write here..."
                        size="small"
                        disabled={isExistingActivity}
                        value={form.description}
                        onChange={(e) => {
                            const value = e.target.value.slice(0, 300);
                            handleChange("description", value);
                            if (value.trim()) {
                                setErrors((prev: any) => {
                                    const { description, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                        error={!!errors.description}
                        helperText={
                            errors.description && isDark
                                ? <span style={errorTextStyle}>{errors.description}</span>
                                : errors.description
                        }
                        FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
                        sx={{
                            ...inputSx,
                            ...(isDark && isExistingActivity && {
                                '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#94a3b8',
                                    WebkitTextFillColor: '#94a3b8',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }),
                        }}
                        inputProps={{ maxLength: 300 }}
                        InputProps={{
                            endAdornment: (
                                <>
                                    {form.description && !isExistingActivity && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                aria-label="clear description"
                                                onClick={() => handleChange("description", "")}
                                                edge="end"
                                                sx={{ color: '#6b7280', padding: 0, marginRight: '2px', '&:hover': { background: 'rgba(0,0,0,0.04)', color: '#6b7280' } }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )}
                                </>
                            ),
                        }}
                    />
                    <Typography variant="caption" className="character-count" sx={{ fontSize: '11px', mt: 0.5, ml: 0 }}>
                        {form.description.length}/300 characters
                    </Typography>
                </div>

                {/* 5. Point of Contact */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Point of Contact<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={employees
                            ? employees.slice().sort((a: any, b: any) => {
                                const na = String(a.displayName || a.name || a.empName || '').trim().toLowerCase();
                                const nb = String(b.displayName || b.name || b.empName || '').trim().toLowerCase();
                                return na.localeCompare(nb);
                            })
                            : []}
                        disabled={isExistingActivity}
                        value={
    form.pointOfContactId
        ? employees.find(e =>
            e.empCode === form.pointOfContactId ||
            e.employeeId === form.pointOfContactId ||
            e.id === form.pointOfContactId
        ) || null
        : null
}

inputValue={pocInput}
onInputChange={(_, v) => setPocInput(v)}

getOptionLabel={(o: any) => typeof o === 'string' ? o : o?.displayName || o?.name || ""}
                        isOptionEqualToValue={(option: any, value: any) =>
                            (option.empCode || option.employeeId || option.id) ===
                            (value.empCode || value.employeeId || value.id)
                        }
                        onChange={(_, v: any) => {
                            setForm((prev: any) => ({
                                ...prev,
                                pointOfContactId: v?.empCode || v?.employeeId || v?.id || 0
                            }));
                            setPocInput(v?.displayName || v?.name || "");
                            if (v?.empCode || v?.employeeId || v?.id) {
                                setErrors((prev: any) => {
                                    const { poc, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}
                        clearOnEscape
                        clearIcon={null}
                        size="small"
                        renderInput={(params) => (
    <TextField
        {...params}
        placeholder="Select"
        error={!!errors.poc}
        helperText={
            errors.poc && isDark
                ? <span style={errorTextStyle}>{errors.poc}</span>
                : errors.poc
        }
        FormHelperTextProps={{
    style: {
        ...errorTextStyle,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
    }
}}
        sx={inputSx}
    InputProps={{
        ...params.InputProps,
        endAdornment: (
            <>
                {pocInput && !isExistingActivity && (
                    
                        <IconButton
                            size="small"
                            onClick={() => {
                                setPocInput("");
                                handleChange("pointOfContactId", 0);
                            }}
                            edge="end"
                            sx={{
                                color: '#6b7280',
                                padding: 0,
                                marginRight: '-28px',
                            }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    
                )}

                {params.InputProps.endAdornment}
            </>
        ),
    }}
                            />
                        )}
                    />
                </div>

                {/* 6. Status */}
                <div style={{ ...fieldWrap, marginBottom: 0 }}>
                    <label style={labelStyle}>Status</label>
                    <Select
                        fullWidth
                        displayEmpty
                        value={form.status || "Yet to start"}
                        size="small"
                        disabled
                        sx={statusStyle}
                        renderValue={(val) => val || <span style={{ color: '#9e9e9e', fontSize: '8px' }}>Yet to start</span>}
                        MenuProps={{
                            PaperProps: {
                                sx: dropdownMenuSx,
                            },
                        }}
                    >
                        <MenuItem value="Yet to start" sx={{ fontSize: '12px', ...dropdownMenuSx }}>Yet to start</MenuItem>
                    </Select>
                </div>

            </DialogContent>

            {/* ── Footer Buttons ── */}
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={saving}
                    sx={{
                        backgroundColor: isDark ? '#38bdf8' : '#124B84',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: isDark ? '#0ea5e9' : '#0f3f70' },
                        '&:disabled': { backgroundColor: '#bdbdbd' },
                    }}
                >
                    {saving ? "Saving..." : isExistingActivity ? "Assign" : "Assign"}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => { resetForm(); onClose(); }}
                    sx={{
                        backgroundColor: isDark ? '#e53935' : '#e53935',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 3,
                        '&:hover': { backgroundColor: isDark ? '#c62828' : '#c62828' },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>

            {/* Snackbar */}
            {snackbar.open && (
                <Box
                    sx={{
                        position: "fixed", bottom: 20, left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "#d32f2f", color: "#fff",
                        px: 3, py: 1, borderRadius: 2, zIndex: 9999,
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        setSnackbar({ open: false, message: "" });
                        if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
                    }}
                >
                    <span className="app-snackbar-message">{snackbar.message}</span>
                </Box>
            )}
        </Dialog>
    );
};

export default AssignActivityModal;